import React from 'react'
import Head from 'next/head'
import { seoConfig, seoConfigProps, seoConfigSST } from '@/assets/configs'
import { useComponentConfig } from '@/hooks'
import { usePetsittingMode } from '@/store/usePetsittingMode'

type Props = {
  children?: JSX.Element
  url?: string
  openGraphType?: string
  title?: string
  subtitle?: string | null
  description?: string
  image?: string
  createdAt?: string
  updatedAt?: string
  noindex?: boolean
  nofollow?: boolean
}

const SEO: React.FC<Props> = ({
  children,
  url = '/',
  openGraphType = 'website',
  title,
  subtitle,
  description,
  image,
  createdAt = new Date().toISOString(),
  updatedAt = new Date().toISOString(),
  noindex = false,
  nofollow = false,
}) => {
  const { isPetsittingMode } = usePetsittingMode()

  const seoProps: seoConfigProps = useComponentConfig(isPetsittingMode ? seoConfigSST : seoConfig, 'common')

  const finalTitle = title || seoProps.title
  const finalSubtitle = subtitle || seoProps.subtitle
  const finalDescription = description || seoProps.description
  const finalImage = image || seoProps.image
  const social = seoProps.social

  const fullTitle = `${finalSubtitle} - ${finalTitle}`

  const robotsContent = [noindex ? 'noindex' : 'index', nofollow ? 'nofollow' : 'follow'].join(', ')

  const metaTags = [
    { name: 'description', content: finalDescription },

    // Open Graph (Facebook, LinkedIn, etc.)
    { property: 'og:locale', content: 'fr_FR' },
    { property: 'og:type', content: openGraphType },
    { property: 'og:title', content: fullTitle },
    { property: 'og:description', content: finalDescription },
    { property: 'og:url', content: url },
    { property: 'og:image', content: finalImage },
    { property: 'og:site_name', content: finalTitle },

    // Facebook publisher
    {
      property: 'article:publisher',
      content: social.facebook,
    },

    // Dates (useful for articles)
    { property: 'article:published_time', content: createdAt },
    { property: 'article:modified_time', content: updatedAt },
  ]

  return (
    <Head>
      <title>{fullTitle}</title>

      {(noindex || nofollow) && <meta name="robots" content={robotsContent} />}

      {metaTags.map(({ name, property, content }) => (
        <meta key={name || property} {...(name ? { name } : { property })} content={content} />
      ))}

      {/* JSON-LD (SEO social + branding) */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: finalTitle,
            url,
            logo: finalImage,
            sameAs: [social.facebook, social.instagram],
          }),
        }}
      />

      {children}
    </Head>
  )
}

export default React.memo(SEO)
